import React, { CSSProperties } from 'react';
import { Row, Col, Space, Badge } from 'antd';
import { Link } from 'react-router';
import { IPlan } from '../../types/plans';

const imageList: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover' as const,
  cursor: 'pointer',
  minHeight: '400px',
};

const metaPlanList: CSSProperties = {
  fontSize: 16,
  fontWeight: 300,
  lineHeight: '27px',
  fontFamily: 'Open Sans, sans-serif',
  color: '#4a4a4a',
  display: 'flex',
  alignItems: 'center',
  letterSpacing: '0.03em',
};

const titlePlanList: CSSProperties = {
  fontSize: '20px',
  lineHeight: '27px',
  fontWeight: 330,
  color: '#000000',
  marginBottom: '24px',
  fontFamily: 'Open Sans, sans-serif',
  letterSpacing: '0.03em',
};

const featureList: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginTop: '16px',
};

const actionButton: CSSProperties = {
  width: '100%',
  height: '60px',
  border: '1px solid #ee8865',
  padding: '2px',
  fontWeight: 300,
  fontSize: '16px',
  lineHeight: '22px',
  fontFamily: 'Open Sans, sans-serif',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  letterSpacing: '0.03em',
  margin: '5px 0',
  cursor: 'pointer',
  transition: 'opacity 0.3s',
  borderRadius: '8px',
  color: '#000000',
};

const planCardStyle: CSSProperties = {
  width: '100%',
  border: 'solid 1px',
  borderRadius: '8px',
  borderColor: '#e8e8e8',
  overflow: 'hidden',
  minHeight: '400px',
};

interface IPlanProps {
  item: IPlan;
  viewMode?: 0 | 1;
  role?: string;
}

interface IListItem {
  label: string;
  exists: boolean;
}

export const Plan: React.FC<IPlanProps> = ({ item, viewMode = 0, role }) => {
  const { meta, isHidden, url = '', decorId = '', planId = '' } = item;
  const hasConfigurator = url.indexOf('configurator') > 0;
  const numberOfPublished = item.photos?.filter(photo => photo.isPublish).length || 0;

  const adminListItems: IListItem[] = [
    { label: 'Configurator', exists: hasConfigurator },
    { label: 'Decor', exists: Boolean(decorId) },
    { label: 'Plan', exists: Boolean(planId) },
    { label: 'Renders', exists: Boolean(numberOfPublished > 1) },
    { label: 'Visualizations', exists: Boolean(numberOfPublished > 5) },
  ];

  return (
    <Row style={planCardStyle}>
      <Col span={24} lg={16}>
        <img
          style={{ ...imageList, opacity: isHidden ? 0.6 : 1.0 }}
          alt={item.title}
          src={
            viewMode === 0 ? item.logo || item.image : `https://3d.homewip.com/svg/${item.planId}/`
          }
        />
      </Col>
      <Col span={24} lg={8} style={{ padding: '24px' }}>
        <div>
          <h1 style={titlePlanList}>{(item.title || 'House').toUpperCase()}</h1>

          {isHidden && <span style={metaPlanList}>Plan is hidden</span>}

          {meta?.outerArea && (
            <span style={metaPlanList}>Exterior area: {meta.outerArea} sq ft</span>
          )}
          {meta?.totalArea && (
            <span style={metaPlanList}>Interior area: {meta.totalArea} sq ft</span>
          )}
          {meta?.width && <span style={metaPlanList}>Width: {meta.width}&apos;</span>}
          {meta?.length && <span style={metaPlanList}>Length: {meta.length}&apos;</span>}
          <br />
          {meta?.bathroomCount && (
            <span style={metaPlanList}>
              Bathroom: {meta.bathroomCount} (
              {meta.bathroom?.map(room => `${room.area}'`).join(', ')})
            </span>
          )}
          {meta?.bedroomCount && (
            <span style={metaPlanList}>
              Bedroom: {meta.bedroomCount} ({meta.bedroom?.map(room => `${room.area}'`).join(', ')})
            </span>
          )}
          {meta?.kitchenCount && (
            <span style={metaPlanList}>
              Living room: {meta.kitchenCount} (
              {meta.kitchen?.map(room => `${room.area}'`).join(', ')})
            </span>
          )}
          <br />
          {role === 'ADMIN' && meta?.type && <span style={metaPlanList}>Type: {meta.type}</span>}

          {role === 'ADMIN' && (
            <div style={featureList}>
              {adminListItems.map(item => (
                <Space key={item.label}>
                  <Badge color={item.exists ? '#52c41a' : '#f5222d'} />
                  <span>{item.label}</span>
                </Space>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: 'auto', paddingTop: '24px' }}>
          <Link
            to={`/plan/${item.id}`}
            target="_blank"
            style={{
              ...actionButton,
              color: '#000000',
              textDecoration: 'none',
            }}>
            View
          </Link>
          {role === 'ADMIN' && (
            <>
              <Link
                to={`/admin/plan/${item.id}`}
                target="_blank"
                style={{
                  ...actionButton,
                  backgroundColor: '#F08B6B',
                  color: '#ffffff',
                  textDecoration: 'none',
                }}>
                Admin View
              </Link>
              <Link
                to={`/admin/plan/edit/${item.id}`}
                target="_blank"
                style={{
                  ...actionButton,
                  backgroundColor: '#F08B6B',
                  color: '#ffffff',
                  textDecoration: 'none',
                }}>
                Edit
              </Link>
            </>
          )}
        </div>
      </Col>
    </Row>
  );
};
